import { useState, useEffect } from 'react';
import { Server } from 'lucide-react';

interface TechStackIconProps {
  tech: string;
  className?: string;
  size?: number;
  fallbackIcon?: React.ComponentType<any>;
}

// Mapping of tech names to Gilbarbara icon names
const techIconMap: Record<string, string> = {
  // Frontend
  'react': 'react',
  'vue': 'vue',
  'vue.js': 'vue',
  'angular': 'angular',
  'svelte': 'svelte',
  'next.js': 'nextjs',
  'nextjs': 'nextjs',
  'typescript': 'typescript',
  'javascript': 'javascript',
  'tailwind': 'tailwindcss',
  'tailwind css': 'tailwindcss',
  'tailwindcss': 'tailwindcss',
  'sass': 'sass',
  'scss': 'sass',
  'vite': 'vitejs',
  'webpack': 'webpack',
  'framer-motion': 'framer',
  'framer': 'framer',
  'html': 'html-5',
  'html5': 'html-5',
  'css': 'css-3',
  'css3': 'css-3',
  'bootstrap': 'bootstrap',
  'jquery': 'jquery',
  
  // Backend
  'node.js': 'nodejs',
  'nodejs': 'nodejs',
  'python': 'python',
  'django': 'django',
  'flask': 'flask',
  'express': 'express',
  'expressjs': 'express',
  'fastapi': 'fastapi',
  'ruby': 'ruby',
  'rails': 'rails',
  'php': 'php',
  'laravel': 'laravel',
  'go': 'go',
  'golang': 'go',
  'rust': 'rust',
  'java': 'java',
  'spring': 'spring',
  'dotnet': 'dotnet',
  '.net': 'dotnet',
  'c#': 'c-sharp',
  'csharp': 'c-sharp',
  
  // Database
  'postgresql': 'postgresql',
  'postgres': 'postgresql',
  'mysql': 'mysql',
  'mongodb': 'mongodb',
  'mongo': 'mongodb',
  'redis': 'redis',
  'sqlite': 'sqlite',
  'supabase': 'supabase',
  'firebase': 'firebase',
  'prisma': 'prisma',
  'graphql': 'graphql',
  
  // DevOps & Cloud
  'docker': 'docker',
  'kubernetes': 'kubernetes',
  'k8s': 'kubernetes',
  'aws': 'aws',
  'amazon web services': 'aws',
  'gcp': 'google-cloud',
  'google cloud': 'google-cloud',
  'azure': 'azure',
  'vercel': 'vercel',
  'netlify': 'netlify',
  'heroku': 'heroku',
  'github': 'github',
  'gitlab': 'gitlab',
  'bitbucket': 'bitbucket',
  'jenkins': 'jenkins',
  'terraform': 'terraform',
  'ansible': 'ansible',
  
  // Mobile
  'react native': 'react',
  'react-native': 'react',
  'flutter': 'flutter',
  'swift': 'swift',
  'kotlin': 'kotlin',
  'expo': 'expo',
  'ionic': 'ionic',
  
  // Tools & Others
  'git': 'git',
  'github actions': 'github-actions',
  'figma': 'figma',
  'sketch': 'sketch',
  'adobe xd': 'adobe-xd',
  'photoshop': 'photoshop',
  'illustrator': 'illustrator',
  'stripe': 'stripe',
  'openai': 'openai',
  'elasticsearch': 'elasticsearch',
  'nginx': 'nginx',
  'apache': 'apache',
  'linux': 'linux',
  'ubuntu': 'ubuntu',
  'centos': 'centos',
  'vim': 'vim',
  'vscode': 'visual-studio-code',
  'webstorm': 'webstorm',
  'intellij': 'intellij',
  'socket.io': 'socketio',
  'socketio': 'socketio',
};

// Generate Gilbarbara CDN URL (primary source)
const getGilbarbaraUrl = (iconName: string): string => {
  return `https://raw.githubusercontent.com/gilbarbara/logos/main/logos/${iconName}.svg`;
};

// Alternative CDN for devicon icons
const getDeviconUrl = (iconName: string): string => {
  return `https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/${iconName}/${iconName}-original.svg`;
};

export function TechStackIcon({ 
  tech, 
  className = '', 
  size = 32,
  fallbackIcon: FallbackIcon = Server
}: TechStackIconProps) {
  const [imageError, setImageError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentUrl, setCurrentUrl] = useState('');
  
  // Normalize tech name for lookup
  const normalizedTech = tech.toLowerCase().trim();
  const iconName = techIconMap[normalizedTech];
  
  useEffect(() => {
    setImageError(false);
    setIsLoading(true);
    if (iconName) {
      const primaryUrl = getGilbarbaraUrl(iconName);
      setCurrentUrl(primaryUrl);
    }
  }, [tech, iconName]);

  // If no icon mapping found, use fallback immediately
  if (!iconName) {
    return (
      <div 
        className={`flex items-center justify-center ${className}`}
        style={{ width: size, height: size }}
        title={tech}
      >
        <FallbackIcon size={Math.max(16, size * 0.6)} />
      </div>
    );
  }

  // If image failed to load, show fallback
  if (imageError) {
    return (
      <div 
        className={`flex items-center justify-center ${className}`}
        style={{ width: size, height: size }}
        title={tech}
      >
        <FallbackIcon size={Math.max(16, size * 0.6)} />
      </div>
    );
  }

  const primaryUrl = getGilbarbaraUrl(iconName);
  const alternativeUrl = getDeviconUrl(iconName);

  return (
    <div 
      className={`flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
      title={tech}
    >
      {isLoading && (
        <div className="animate-pulse bg-gray-200 dark:bg-gray-700 rounded" 
             style={{ width: size, height: size }} />
      )}
      <img
        key={currentUrl} // Force re-render when URL changes
        src={currentUrl}
        alt={tech}
        className={`${isLoading ? 'hidden' : 'block'} object-contain`}
        style={{ width: size, height: size }}
        onLoad={() => {
          setIsLoading(false);
        }}
        onError={(e) => {
          const currentTarget = e.currentTarget as HTMLImageElement;
          
          if (currentTarget.src === primaryUrl) {
            setCurrentUrl(alternativeUrl);
          } else {
            setImageError(true);
            setIsLoading(false);
          }
        }}
      />
    </div>
  );
}

// Utility function to get tech stack icons for a list of technologies
export function getTechStackIcons(techList: string[], size: number = 32, className: string = '') {
  return techList.map((tech, index) => (
    <TechStackIcon
      key={`${tech}-${index}`}
      tech={tech}
      size={size}
      className={className}
    />
  ));
}

// Export the tech icon mapping for external use
export { techIconMap };
